import React from 'react';
import './CaseStudy.css';

/**
 * Shared case-study layout for project detail pages.
 *
 * Renders the six required sections in a fixed order — Problem, Data
 * Sources, Pipeline & Transformation Approach, Tools Used, Outcome &
 * Business Value, How I Would Improve This Today — driven by a project
 * object from src/data/projects.js.
 *
 * Bespoke content (images, diagrams, embeds, extra links) can be dropped
 * between sections via the `slots` prop:
 *   slots.header          — rendered right under the title/tagline
 *   slots.afterProblem
 *   slots.afterDataSources
 *   slots.afterPipeline
 *   slots.afterTools
 *   slots.afterOutcome
 *   slots.afterImprovements
 */
const CaseStudy = ({ project, icons = [], slots = {} }) => {
  if (!project) return null;
  const { title, tagline, sections = {}, links = {} } = project;
  const {
    header,
    afterProblem,
    afterDataSources,
    afterPipeline,
    afterTools,
    afterOutcome,
    afterImprovements,
  } = slots;

  return (
    <section className="case-study-page">
      <div className="case-study-content">
        {icons.length > 0 && (
          <div className="case-study-icon-row">
            {icons.map((icon, idx) => (
              <img key={idx} src={icon.src} alt={icon.alt} className="case-study-icon" />
            ))}
          </div>
        )}

        <h1>{title}</h1>
        {tagline && <p className="case-study-tagline">{tagline}</p>}

        {header}

        {sections.problem && (
          <section className="case-study-section case-study-card">
            <h2>Problem</h2>
            <p>{sections.problem}</p>
          </section>
        )}
        {afterProblem}

        {sections.dataSources && (
          <section className="case-study-section case-study-card">
            <h2>Data Sources</h2>
            <p>{sections.dataSources}</p>
          </section>
        )}
        {afterDataSources}

        {sections.pipeline && (
          <section className="case-study-section case-study-card">
            <h2>Pipeline &amp; Transformation Approach</h2>
            <p>{sections.pipeline}</p>
          </section>
        )}
        {afterPipeline}

        {sections.tools && sections.tools.length > 0 && (
          <section className="case-study-section">
            <h2>Tools Used</h2>
            <div className="case-study-pill-row">
              {sections.tools.map((tool) => (
                <span className="case-study-pill" key={tool}>
                  {tool}
                </span>
              ))}
            </div>
          </section>
        )}
        {afterTools}

        {sections.outcome && (
          <section className="case-study-section case-study-card">
            <h2>Outcome &amp; Business Value</h2>
            <p>{sections.outcome}</p>
          </section>
        )}
        {afterOutcome}

        {sections.improvements && sections.improvements.length > 0 && (
          <section className="case-study-section case-study-card">
            <h2>How I Would Improve This Today</h2>
            <ul>
              {sections.improvements.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </section>
        )}
        {afterImprovements}

        {links.github && (
          <div className="case-study-links">
            <a
              className="case-study-button"
              href={links.github}
              target="_blank"
              rel="noopener noreferrer"
            >
              View the project on GitHub
            </a>
          </div>
        )}
      </div>
    </section>
  );
};

export default CaseStudy;
